import React from "react";
import Button from "@amzn/meridian/button";
import Box from "@amzn/meridian/box";
import { Space } from "../../space/Space";
import Text from "@amzn/meridian/text";
import Row from "@amzn/meridian/row";
import LoaderBox from "../../loader/LoaderBox";
import { useAppSelector } from "../../../app/store/hooks";
import { useUpdateAppealMutation } from "../../../endpoints/updateAppeal";
import { useNavigate, useParams } from "react-router-dom";
import { SelectImagesProps } from "./utils/types";
import { useImageSelection, useImageUrls, useImageExpansion, useUpdateAppeal } from "./utils/hooks";
import { ImageTable } from "./utils/ImageTable";
import { ExpandedImage } from "./utils/ExpandedImage";
import "./SelectImages.css";
import { handleBackToProperty } from "../utils/utils";

export const SelectImages: React.FC<SelectImagesProps> = ({ onNext, onPrevious, data }) => {
    const navigate = useNavigate();
    const { propertyId } = useParams();
    const [updateAppeal, { isLoading: isUpdating }] = useUpdateAppealMutation();
    const { alias } = useAppSelector(state => state.auth.user);

    const images =
        data?.appealDetails?.appealApproachCalculationData.exportAppealsPackage.mapsAndImages || [];
    const { selected, onChangeRow, handleSelectAll, handleClearSelection } =
        useImageSelection(images);
    const imageUrls = useImageUrls(images);
    const {
        expandedImage,
        isImageLoading,
        zoomLevel,
        handleExpandImage,
        handleCloseExpanded,
        handleZoom,
        setIsImageLoading,
    } = useImageExpansion();
    const handleUpdateAppeal = useUpdateAppeal(updateAppeal, data, alias, onNext);

    const handleBack = () => {
        handleBackToProperty({
            data,
            propertyId: propertyId!,
            alias,
            updateAppeal,
            navigate,
        });
    };

    if (isUpdating) {
        return <LoaderBox loadingText="Loading Appeal Data..." />;
    }

    return (
        <>
            <ExpandedImage
                expandedImage={expandedImage}
                isImageLoading={isImageLoading}
                zoomLevel={zoomLevel}
                handleCloseExpanded={handleCloseExpanded}
                handleZoom={handleZoom}
                setIsImageLoading={setIsImageLoading}
            />

            <Box>
                <Space size={"l"} />
                <Row alignmentHorizontal={"justify"}>
                    <Box>
                        <Text type={"h500"}>Select Maps and Images</Text>
                        <Text>Select Maps and Images to be included in the Appeals package</Text>
                    </Box>
                    <Row alignmentHorizontal={"end"}>
                        <Button type={"secondary"} onClick={handleClearSelection}>
                            Clear Selection
                        </Button>
                        <Button type={"secondary"} onClick={handleSelectAll}>
                            Select All
                        </Button>
                    </Row>
                </Row>
                <Space size={"s"} />

                <Box type={"outline"} spacingInset={"500"}>
                    {images.length === 0 ? (
                        <Text>No images available</Text>
                    ) : (
                        <ImageTable
                            images={images}
                            selected={selected}
                            imageUrls={imageUrls}
                            onChangeRow={onChangeRow}
                            handleExpandImage={handleExpandImage}
                        />
                    )}
                </Box>

                <Space size={"s"} />
                <Row alignmentHorizontal={"end"} spacingInset={"200"}>
                    <Button type={"link"} size={"large"} onClick={handleBack}>
                        Back to Property
                    </Button>
                    <Button size={"large"} type={"secondary"} onClick={onPrevious}>
                        Back
                    </Button>
                    <Button size={"large"} onClick={() => handleUpdateAppeal(selected)}>
                        Next
                    </Button>
                </Row>
            </Box>
        </>
    );
};
